'use client'

import Link from 'next/link'
import { Logo } from '@/components/ui/logo'
import { LanguageSelector } from '@/components/ui/language-selector'
import { useTranslations } from 'next-intl'

interface AppFooterProps {
  className?: string
  variant?: 'default' | 'minimal'
  /** Path to the merch page — when provided, a "Merch" link appears in the minimal footer */
  merchHref?: string
}

/**
 * Shared footer component for use across the app
 * - default: Full footer with logo and links
 * - minimal: Compact footer with just links
 */
export function AppFooter({ className, variant = 'default', merchHref }: AppFooterProps) {
  const t = useTranslations('footer')
  const currentYear = new Date().getFullYear()

  if (variant === 'minimal') {
    return (
      <footer className={`border-t border-zinc-800/50 py-4 px-4 mt-auto print:hidden ${className || ''}`}>
        <div className="container mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-xs text-zinc-400">
            <Link href="/terms" className="hover:text-zinc-300 transition-colors">
              {t('terms')}
            </Link>
            <span className="hidden sm:inline">•</span>
            <Link href="/privacy" className="hover:text-zinc-300 transition-colors">
              {t('privacy')}
            </Link>
            {merchHref && (
              <>
                <span className="hidden sm:inline">•</span>
                <Link href={merchHref} className="hover:text-zinc-300 transition-colors">
                  {t('merch')}
                </Link>
              </>
            )}
            <span className="hidden sm:inline">•</span>
            <LanguageSelector />
            <span className="hidden sm:inline">•</span>
            <span>{t('copyright', { year: currentYear })}</span>
          </div>
        </div>
      </footer>
    )
  }

  return (
    <footer className={`border-t border-zinc-800 py-6 px-4 mt-auto print:hidden ${className || ''}`}>
      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo size="sm" />
          <div className="flex items-center gap-6 text-sm text-zinc-400">
            <Link href="/terms" className="hover:text-zinc-300 transition-colors">
              {t('terms')}
            </Link>
            <Link href="/privacy" className="hover:text-zinc-300 transition-colors">
              {t('privacy')}
            </Link>
            <LanguageSelector />
            <span>{t('copyright', { year: currentYear })}</span>
          </div>
        </div>
      </div>
    </footer>
  )
}
